/**
 * Which of the sheet's three sections are open, remembered per meal.
 *
 * The sheet grew a third section — Stored, above the usuals — and three
 * headings is the point at which a fixed arrangement stops being right for
 * everybody. Somebody who batch-cooks on Sunday wants the pots at the top of
 * dinner; somebody who never has wants that line gone, and wants it gone *for
 * good* rather than every time they open the sheet.
 *
 * Per meal and not per session, because the arrangement is about a meal:
 * breakfast has no pots in it and lunch is nothing but leftovers, and folding
 * Stored away in one says nothing about the other. This mirrors `open-meals.ts`
 * on Today, deliberately — the same preference, the same storage, the same
 * reason it survives a reload.
 *
 * Absent means "never arranged", and the caller opens everything. That is not
 * the same as "arranged to be empty", which a person who collapsed all three is
 * entitled to keep.
 *
 * Every access is wrapped. Storage throws rather than returning null in Safari
 * with site data blocked, and a private window is not a reason the add sheet
 * cannot open.
 */

import type { Meal } from "@calorio/core";

const KEY = "calorio:sheet-sections";

/** The sheet's collapsible headings, in the order they appear. */
export type SheetSection = "stored" | "usuals" | "recent";

export const ALL_SECTIONS: SheetSection[] = ["stored", "usuals", "recent"];

type Stored = Partial<Record<Meal, SheetSection[]>>;

function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

/** The sections open for a meal, or null if this meal has never been arranged. */
export function openSections(meal: Meal): SheetSection[] | null {
  const stored = read()[meal];
  return Array.isArray(stored) ? stored : null;
}

export function setOpenSections(meal: Meal, sections: SheetSection[]): void {
  try {
    const all = read();
    all[meal] = sections;
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // The arrangement still works for this visit; it just does not survive
    // leaving, which is where this started.
  }
}
